import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Chip, Tooltip } from '@mui/material'
import { TagAutocomplete } from './TagAutocomplete'
import { IconButton, ButtonProps } from '../buttons/IconButton'
import { useLabel } from '../hooks/useLabel'
import { Icon } from './Icon'

type Props = {
  tags?: ITag[]
  filter?: ITag[]
  label?: string
  placeholder?: string
  allowAdding?: boolean
  hideIcons?: boolean
  createOnly?: boolean
  button?: string
  buttonProps?: ButtonProps
  disabled?: boolean
  keyboardShortcut?: boolean
  onCreate?: (tag: ITag) => void
  onSelect?: (tag: ITag) => void
}

export const TagEditor: React.FC<Props> = ({
  tags = [],
  filter = [],
  label,
  placeholder,
  allowAdding = true,
  hideIcons,
  createOnly,
  button,
  buttonProps,
  disabled,
  keyboardShortcut = true,
  onCreate,
  onSelect,
}) => {
  const { t } = useTranslation()
  const resolvedLabel = label ?? t('tagEditor.label', 'TAG')
  const resolvedPlaceholder = placeholder ?? t('tagEditor.placeholder', 'New tag...')
  const getColor = useLabel()
  const [open, setOpen] = React.useState<boolean>(false)
  const [creating, setCreating] = React.useState<boolean>(false)
  const buttonRef = React.useRef<HTMLButtonElement>(null)
  const chipRef = React.useRef<HTMLDivElement>(null)

  const handleOpen = () => setOpen(!open)
  const handleClose = () => setOpen(false)

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === '#') {
      event.preventDefault()
      handleOpen()
    }
  }

  useEffect(() => {
    if (keyboardShortcut) window.addEventListener('keydown', onKeyDown)
    return () => {
      if (keyboardShortcut) window.removeEventListener('keydown', onKeyDown)
    }
  }, [])

  return (
    <>
      {button ? (
        <IconButton
          {...buttonProps}
          ref={buttonRef}
          icon={button}
          loading={creating}
          onClick={handleOpen}
          disabled={open || tags.length === 0 || buttonProps?.disabled || disabled}
          forceTitle
        />
      ) : (
        <Chip
          label={
            <>
              <Icon name={creating ? 'spinner-third' : 'plus'} spin={creating} size="sm" inlineLeft />
              {resolvedLabel}
            </>
          }
          sx={{ fontWeight: 500, letterSpacing: 1, color: 'grayDarker.main' }}
          size="small"
          onClick={handleOpen}
          ref={chipRef}
        />
      )}
      <TagAutocomplete
        items={tags}
        open={open}
        indicator="tag"
        hideIcons={hideIcons}
        filter={filter}
        createOnly={createOnly}
        placeholder={resolvedPlaceholder}
        targetEl={buttonRef.current || chipRef.current}
        onItemColor={tag => getColor(tag.color)}
        onClose={handleClose}
        allowAdding={allowAdding}
        InputProps={{
          endAdornment: keyboardShortcut && (
            <Tooltip title={t('tagEditor.keyboardShortcut', "Keyboard shortcut '#'")} placement="top" arrow>
              <Chip label="#" size="small" />
            </Tooltip>
          ),
        }}
        onSelect={async (action, tag) => {
          if (action === 'new') {
            setCreating(true)
            await onCreate?.(tag)
            setCreating(false)
          }
          await onSelect?.(tag)
        }}
      />
    </>
  )
}

